/**
 * Helpers for POST /session/load-user-session and related AINA session payloads.
 */

export const AINA_AGENT_VOICE = "AINA_VOICE";
export const AINA_AGENT_MENTOR = "AINA_MENTOR";

function visitForSessionId(node: unknown, depth = 0): string | undefined {
  if (depth > 8 || node == null) return undefined;
  if (typeof node !== "object") return undefined;
  const r = node as Record<string, unknown>;
  const directKeys = [
    "conversation_id",
    "conversationId",
    "session_id",
    "sessionId",
    "chat_session_id",
    "chatSessionId",
  ] as const;
  for (const k of directKeys) {
    const v = r[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  for (const nested of ["data", "session", "user_session", "result"]) {
    const inner = visitForSessionId(r[nested], depth + 1);
    if (inner) return inner;
  }
  return undefined;
}

/** Resolves conversation / session id for `/{userId}/{sessionId}/history`. */
export function extractAinaConversationSessionId(
  loadSessionResponse: unknown,
): string | null {
  const id = visitForSessionId(loadSessionResponse);
  return id ?? null;
}

function normalizeAgentString(raw: string): string {
  const t = raw.trim();
  if (!t) return AINA_AGENT_VOICE;
  const u = t.toUpperCase();
  if (u === AINA_AGENT_MENTOR || u.includes("MENTOR")) return AINA_AGENT_MENTOR;
  if (u === AINA_AGENT_VOICE || u.includes("VOICE")) return AINA_AGENT_VOICE;
  return t;
}

/**
 * Reads landing agent from load-user-session JSON (flexible nesting).
 */
export function parseAinaAgentFromLoadSessionResponse(data: unknown): string {
  const visit = (node: unknown): string | undefined => {
    if (node == null) return undefined;
    if (typeof node === "string") return normalizeAgentString(node);
    if (typeof node !== "object" || Array.isArray(node)) return undefined;
    const r = node as Record<string, unknown>;
    const keys = [
      "agent",
      "agent_type",
      "agentType",
      "assigned_agent",
      "assignedAgent",
      "default_agent",
      "defaultAgent",
      "landing_agent",
      "landingAgent",
    ] as const;
    for (const k of keys) {
      const v = r[k];
      if (typeof v === "string" && v.trim()) {
        return normalizeAgentString(v);
      }
    }
    for (const nested of ["data", "session", "user_session", "result"]) {
      const inner = visit(r[nested]);
      if (inner != null) return inner;
    }
    return undefined;
  };
  return visit(data) ?? AINA_AGENT_VOICE;
}

const INTRO_MESSAGE_KEYS = [
  "intro_message",
  "introMessage",
  "welcome_message",
  "welcomeMessage",
  "greeting",
] as const;

function readIntroFromRecord(r: Record<string, unknown>): string | undefined {
  for (const k of INTRO_MESSAGE_KEYS) {
    const v = r[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

/**
 * Reads intro / welcome copy from load-user-session JSON (flexible nesting).
 */
export function parseAinaIntroMessageFromLoadSessionResponse(
  data: unknown,
  fallback = "",
): string {
  const visit = (node: unknown, depth = 0): string | undefined => {
    if (depth > 8 || node == null) return undefined;
    if (typeof node !== "object" || Array.isArray(node)) return undefined;
    const r = node as Record<string, unknown>;
    const direct = readIntroFromRecord(r);
    if (direct) return direct;
    for (const nested of [
      "data",
      "session",
      "user_session",
      "result",
      "user_info",
    ]) {
      const inner = visit(r[nested], depth + 1);
      if (inner) return inner;
    }
    return undefined;
  };
  return visit(data) ?? fallback;
}

function readBoolish(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
  }
  return undefined;
}

const NEW_SESSION_KEYS = [
  "is_new_session",
  "isNewSession",
  "new_session",
  "newSession",
] as const;

function readNewSessionFromRecord(
  r: Record<string, unknown>,
): boolean | undefined {
  for (const k of NEW_SESSION_KEYS) {
    const b = readBoolish(r[k]);
    if (b !== undefined) return b;
  }
  return undefined;
}

/**
 * Reads whether the AINA host should treat this as a new session (load-user-session).
 */
export function parseAinaIsNewSessionFromLoadSessionResponse(
  data: unknown,
  fallback = true,
): boolean {
  const visit = (node: unknown, depth = 0): boolean | undefined => {
    if (depth > 8 || node == null) return undefined;
    if (typeof node !== "object" || Array.isArray(node)) return undefined;
    const r = node as Record<string, unknown>;
    const direct = readNewSessionFromRecord(r);
    if (direct !== undefined) return direct;
    for (const nested of ["data", "session", "user_session", "result"]) {
      const inner = visit(r[nested], depth + 1);
      if (inner !== undefined) return inner;
    }
    return undefined;
  };
  return visit(data) ?? fallback;
}
